import React from 'react';
import { AlertTriangle, Bell, FileText, Calendar, CheckCircle, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Notification types with their styling
const NOTIFICATION_TYPES = {
  urgent: {
    icon: AlertTriangle,
    bgColor: 'bg-red-600',
    textColor: 'text-white',
    borderColor: 'border-red-700',
    pulse: true
  },
  drugScreen: {
    icon: FileText,
    bgColor: 'bg-red-600',
    textColor: 'text-white',
    borderColor: 'border-red-700',
    pulse: true
  },
  reminder: {
    icon: Bell,
    bgColor: 'bg-amber-500',
    textColor: 'text-white',
    borderColor: 'border-amber-600',
    pulse: false
  },
  task: {
    icon: Calendar,
    bgColor: 'bg-blue-500',
    textColor: 'text-white',
    borderColor: 'border-blue-600',
    pulse: false
  },
  success: {
    icon: CheckCircle,
    bgColor: 'bg-green-500',
    textColor: 'text-white',
    borderColor: 'border-green-600',
    pulse: false
  },
  info: {
    icon: Bell,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-200',
    pulse: false
  }
};

// Single Notification Item
export function NotificationItem({ 
  type = 'info', 
  title, 
  message, 
  timestamp, 
  onDismiss,
  onClick,
  actionLabel,
  onAction
}) {
  const config = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.info;
  const Icon = config.icon;
  const isUrgent = type === 'urgent' || type === 'drugScreen';

  return (
    <div 
      className={`
        relative rounded-lg border-2 p-4 mb-3 transition-all duration-200
        ${config.bgColor} ${config.textColor} ${config.borderColor}
        ${config.pulse ? 'animate-pulse-slow' : ''}
        ${onClick ? 'cursor-pointer hover:opacity-90' : ''}
      `}
      onClick={onClick}
      data-testid={`notification-${type}`}
    >
      {/* Urgent Badge */}
      {isUrgent && (
        <div className="absolute -top-2 -left-2 bg-white text-red-600 text-xs font-black px-2 py-0.5 rounded-full border-2 border-red-600 uppercase tracking-wider">
          URGENT
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-full ${isUrgent ? 'bg-white/20' : 'bg-white/10'}`}>
          <Icon className="w-5 h-5" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sm uppercase tracking-wide mb-1">
            {title}
          </h4>
          <p className={`text-sm ${isUrgent ? 'text-white/90' : 'opacity-90'}`}>
            {message}
          </p>
          {timestamp && (
            <p className={`text-xs mt-2 ${isUrgent ? 'text-white/70' : 'opacity-70'}`}>
              {timestamp}
            </p>
          )}
          
          {actionLabel && onAction && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAction();
              }}
              className={`
                mt-3 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide
                ${isUrgent 
                  ? 'bg-white text-red-600 hover:bg-red-50' 
                  : 'bg-white/20 hover:bg-white/30'
                }
                transition-colors
              `}
              data-testid={`notification-action-${type}`}
            >
              {actionLabel}
            </button>
          )}
        </div>

        {onDismiss && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            className="p-1 rounded-full hover:bg-white/20 transition-colors"
            data-testid="notification-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// Notification List Container
export function NotificationList({ notifications = [], onDismiss, onAction }) {
  const { t } = useLanguage();
  
  if (notifications.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No notifications</p>
      </div>
    );
  }

  // Sort urgent notifications to top
  const sortedNotifications = [...notifications].sort((a, b) => {
    const urgentTypes = ['urgent', 'drugScreen'];
    const aIsUrgent = urgentTypes.includes(a.type);
    const bIsUrgent = urgentTypes.includes(b.type);
    
    if (aIsUrgent && !bIsUrgent) return -1;
    if (!aIsUrgent && bIsUrgent) return 1;
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  return (
    <div className="space-y-2">
      {sortedNotifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          {...notification}
          onDismiss={onDismiss ? () => onDismiss(notification.id) : undefined}
          onAction={onAction ? () => onAction(notification) : undefined}
        />
      ))}
    </div>
  );
}

// Drug Screen Notification Component (Special urgent notification)
export function DrugScreenNotification({ 
  deadline, 
  labs = [], 
  clientId,
  onUploadResults,
  onDismiss 
}) {
  const { t } = useLanguage();
  
  // Calculate time remaining
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const hoursRemaining = Math.max(0, Math.floor((deadlineDate - now) / (1000 * 60 * 60)));

  return (
    <div 
      className="bg-red-600 border-2 border-red-700 rounded-xl p-5 text-white relative overflow-hidden"
      data-testid="drug-screen-notification"
    >
      {/* Pulsing background effect */}
      <div className="absolute inset-0 bg-red-500 animate-pulse opacity-30" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-white/20 rounded-full">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <div className="bg-white text-red-600 text-xs font-black px-3 py-1 rounded-full uppercase tracking-wider inline-block mb-1">
              {t('notifications.urgent')}
            </div>
            <h3 className="text-lg font-bold uppercase tracking-wide">
              {t('notifications.drugScreenRequest')}
            </h3>
          </div>
        </div>

        {/* Message */}
        <p className="text-white/90 mb-4">
          {t('notifications.attendWithin24Hours')}
        </p>

        {/* Time Remaining */}
        <div className="bg-white/20 rounded-lg p-3 mb-4">
          <p className="text-xs uppercase tracking-wide mb-1 opacity-80">Time Remaining</p>
          <p className="text-2xl font-bold">
            {hoursRemaining} {t('time.hours')}
          </p>
        </div>

        {/* Your ID */}
        {clientId && (
          <div className="bg-white/10 rounded-lg p-3 mb-4">
            <p className="text-xs uppercase tracking-wide mb-1 opacity-80">{t('drugScreen.yourId')}</p>
            <p className="text-xl font-mono font-bold tracking-wider">{clientId}</p>
          </div>
        )}

        {/* Approved Labs */}
        {labs.length > 0 && (
          <div className="mb-4">
            <p className="text-xs uppercase tracking-wide mb-2 opacity-80">{t('drugScreen.labs')}</p>
            <ul className="space-y-1">
              {labs.map((lab, index) => (
                <li key={index} className="text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-white rounded-full" />
                  {lab}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onUploadResults}
            className="flex-1 bg-white text-red-600 font-bold py-3 px-4 rounded-lg uppercase tracking-wide hover:bg-red-50 transition-colors"
            data-testid="upload-drug-screen-btn"
          >
            {t('drugScreen.uploadResults')}
          </button>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="px-4 py-3 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              data-testid="dismiss-drug-screen-btn"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Notification Badge (for showing count on icons)
export function NotificationBadge({ count = 0, urgent = false }) {
  if (count === 0) return null;

  return (
    <span 
      className={`
        absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center
        text-xs font-bold rounded-full px-1.5
        ${urgent 
          ? 'bg-red-600 text-white animate-pulse' 
          : 'bg-blue-600 text-white'
        }
      `}
      data-testid="notification-badge"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// Add custom animation to tailwind (add to index.css)
export const notificationAnimations = `
@keyframes pulse-slow {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.85;
  }
}
.animate-pulse-slow {
  animation: pulse-slow 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
`;

export default NotificationList;
